import { logger } from "@/lib/logger";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface SitePatientStats {
  totalPatients: number;
  omopMappedCount: number;
  omopMappingRate: number;
  diversityBreakdown: {
    race: Record<string, number>;
    ethnicity: Record<string, number>;
  };
  medicalConditionsCount: number;
}

// Default stats to prevent runtime crashes
const DEFAULT_STATS: SitePatientStats = {
  totalPatients: 0,
  omopMappedCount: 0,
  omopMappingRate: 0,
  diversityBreakdown: {
    race: {},
    ethnicity: {},
  },
  medicalConditionsCount: 0,
};

/**
 * Hook to fetch site patient statistics using the optimized server-side RPC.
 * Uses get_site_patient_stats PostgreSQL function for efficient aggregation.
 */
export function useSitePatientStats(siteId: string) {
  return useQuery({
    queryKey: ['site-patient-stats', siteId],
    queryFn: async (): Promise<SitePatientStats> => {
      const startTime = performance.now();
      
      try {
        // Call the server-side RPC for efficient aggregation
        const { data, error } = await supabase.rpc('get_site_patient_stats', {
          p_site_id: siteId
        });

        if (error) {
          logger.error('[useSitePatientStats] RPC error:', error);
          return DEFAULT_STATS;
        }

        if (!data) {
          logger.warn('[useSitePatientStats] No data returned from RPC');
          return DEFAULT_STATS;
        }

        const duration = performance.now() - startTime;
        logger.debug(`[useSitePatientStats] Completed in ${duration.toFixed(0)}ms via RPC`);

        // Parse the response - RPC returns JSON object
        const stats = data as {
          totalPatients: number;
          omopMappedCount: number;
          omopMappingRate: number;
          medicalConditionsCount: number;
          diversityBreakdown: {
            race: Record<string, number>;
            ethnicity: Record<string, number>;
          };
        };

        return {
          totalPatients: stats.totalPatients ?? 0,
          omopMappedCount: stats.omopMappedCount ?? 0,
          omopMappingRate: stats.omopMappingRate ?? 0,
          medicalConditionsCount: stats.medicalConditionsCount ?? 0,
          diversityBreakdown: {
            race: stats.diversityBreakdown?.race ?? {},
            ethnicity: stats.diversityBreakdown?.ethnicity ?? {},
          },
        };
      } catch (error) {
        logger.error('[useSitePatientStats] Unexpected error:', error);
        return DEFAULT_STATS;
      }
    },
    enabled: !!siteId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });
}
